'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { parseApiAsset } from '@morpho-org/dapp-core'
import { CellTokenAmountPrice, VaultCell } from '@morpho-org/ui-v6/client'
import { CellSimpleText, Skeleton } from '@morpho-org/ui-v6/server'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { abbreviateAddress } from '@repo/utils'
import {
  columnFilteringFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  filterFns,
  rowSortingFeature,
  tableFeatures
} from '@tanstack/react-table'

import { ButtonLink } from '@/components/common/ButtonLink'
import { TimestampCell } from '@/components/common/Cells/TimestampCell'
import { UserCellDashboard } from '@/components/common/Cells/UserCell'
import { InternalLink } from '@/components/common/InternalLink'
import { getParsedValue } from '@/helpers/formatters'

import type { MarketTransactionsTableRow } from './MarketTransactions.types'

import { COLUMN_IDS } from './config'

// Keeps enableSorting: false effective; without rowSortingFeature, accessor headers render as sortable.
export const marketTransactionsTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnVisibilityFeature,
  filteredRowModel: createFilteredRowModel(),
  filterFns,
  rowSortingFeature
})
export type MarketTransactionsTableFeatures = typeof marketTransactionsTableFeatures

export const COLUMN_LABELS: Partial<Record<keyof typeof COLUMN_IDS, string>> = {
  [COLUMN_IDS.Timestamp]: 'Date',
  [COLUMN_IDS.TypeText]: 'Type',
  [COLUMN_IDS.AssetsUsd]: 'Amount',
  [COLUMN_IDS.User]: 'User',
  [COLUMN_IDS.TransactionHash]: 'Transaction'
}

export const columnsDefs: ColumnDefWithSkeleton<
  MarketTransactionsTableFeatures,
  MarketTransactionsTableRow
>[] = [
  {
    id: COLUMN_IDS.Timestamp,
    accessorKey: COLUMN_IDS.Timestamp,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.Timestamp}</span>
        </TooltipTrigger>
        <TooltipContent>Timestamp of when the transaction occurred.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => <TimestampCell timestamp={row.original.timestamp} />,
    skeletonCell: <Skeleton className="h-[22px] w-[122px] rounded-[20px]" />,
    enableSorting: false
  },
  {
    id: COLUMN_IDS.TypeText,
    accessorKey: COLUMN_IDS.TypeText,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.TypeText}</span>
        </TooltipTrigger>
        <TooltipContent>
          The nature of the transaction, providing more details about the type of transaction.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => <CellSimpleText>{row.original.typeText}</CellSimpleText>,
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[120px] rounded-[20px]" />
  },
  {
    id: COLUMN_IDS.AssetsUsd,
    accessorKey: COLUMN_IDS.AssetsUsd,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">{COLUMN_LABELS.AssetsUsd}</span>
        </TooltipTrigger>
        <TooltipContent>The amount of tokens involved in the transaction.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { asset, amount, amountUsd } = row.original
      if (!asset) return null
      const value = getParsedValue(amountUsd, safeParseNumber)
      return (
        <CellTokenAmountPrice
          token={parseApiAsset(asset)}
          amount={amount}
          value={value}
          withAvatar
        />
      )
    },
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[241px] rounded-[20px]" />
  },
  {
    id: COLUMN_IDS.User,
    accessorKey: COLUMN_IDS.User,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14.5px' }}>
            {COLUMN_LABELS.User}
          </span>
        </TooltipTrigger>
        <TooltipContent>User who executed the transaction.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { vault, vaultUrl } = row.original

      if (vault && vaultUrl) {
        return (
          // Mirrors ButtonLink's footprint (px-3 + 0.5px border) so vault logos align with wallet avatars.
          <InternalLink
            href={vaultUrl}
            withHoverEffect
            className="pl-sm border-[0.5px] border-transparent"
          >
            <VaultCell
              nameClassName="max-w-[240px]"
              address={vault.address}
              logoURI={vault.assetLogoUri}
              name={vault.name}
              chainId={row.original.chainId}
              logoSize="small"
              isVaultV2={vault.isV2}
              className="gap-2xs"
            />
          </InternalLink>
        )
      }

      return <UserCellDashboard address={row.original.user.address} />
    },
    enableSorting: false,
    skeletonCell: (
      <Skeleton className="h-[22px] w-[140px] rounded-[20px]" style={{ marginLeft: '14.5px' }} />
    )
  },
  {
    id: COLUMN_IDS.TransactionHash,
    accessorKey: COLUMN_IDS.TransactionHash,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '12px' }}>
            {COLUMN_LABELS.TransactionHash}
          </span>
        </TooltipTrigger>
        <TooltipContent>The transaction hash.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <ButtonLink
        href={row.original.urlHash}
        mode="external"
        buttonProps={{
          variant: 'ghost',
          size: 'default',
          label: abbreviateAddress(row.original.hash)
        }}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[119px] rounded-[20px]" />
  }
]
